import React, {useEffect, useMemo, useRef, useState} from "react";
import {APIProvider, AdvancedMarker, InfoWindow, Map, Polyline, useAdvancedMarkerRef, useMap} from "@vis.gl/react-google-maps";
import {useQuery} from "@tanstack/react-query";
import {Template} from "../../../components/template/template.jsx";
import {useAppShell} from "../../app-shell/app-shell-context.js";
import CorporationService from "../../../services/web/corporation-service.js";
import sentryCloudSearchService from "../../../services/web/sentry-cloud-search-service.js";
import projectUtils from "../../../utils/project-utils.js";
import {useSpiderfy} from "../../../hooks/use-spiderfier-adv-marker.js";
import SentryCloudSearchMapElements from "./sentry-cloud-search-map-elements.jsx";
import {FaRightLong, FaXmark} from "react-icons/fa6";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import Button from "../../../components/button/button.jsx";

const DEFAULT_CENTER = {lat: -23, lng: -46};
const mapIds = projectUtils.googleMapsIds();

const decodeFilters = (encodedFilters) => {
    if (!encodedFilters) return null;

    try {
        const binary = atob(encodedFilters);
        const decoded = decodeURIComponent([...binary].map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""));
        return JSON.parse(decoded);
    } catch {
        return null;
    }
};

const isValidCoordinate = (latitude, longitude) => (
    Number.isFinite(latitude) && Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
);

const formatLocal = ({cpName, rtDescr, laneObs}) => {
    const value = cpName && rtDescr ? `${cpName} - ${rtDescr}${laneObs ? ` - ${laneObs}` : ""}` : cpName || rtDescr || laneObs || "Não informado";

    return value.split("->").map((part, index, parts) => (
        <React.Fragment key={index}>
            {part} {index < parts.length - 1 && ( <FaRightLong className="mx-1 inline transform -translate-y-0.5"/> )}
        </React.Fragment>
    ));
};

const SearchMarker = ({point, isOpen, onOpen, onClose}) => {
    const [markerRef, marker] = useAdvancedMarkerRef();
    const position = point.lat !== undefined && point.lng !== undefined ? {lat: point.lat, lng: point.lng} : point.position;
    const directionImage = point.img ? getAssetPath(point.img.replace("/web/public/assets/", "")) : null;

    return (
        <>
            <AdvancedMarker ref={markerRef} position={position} onClick={() => onOpen(point)}>
                <img src={getAssetPath("img/markers/passage-marker.svg")} alt="" className="h-12 w-12 object-contain" draggable={false}/>
            </AdvancedMarker>

            {isOpen && marker && (
                <InfoWindow
                    anchor={marker}
                    className="sentry-cloud-search-map-info-window"
                    minWidth={210}
                    maxWidth={300}
                    headerContent={(
                        <div className="sentry-cloud-search-map-info-window-header flex w-[calc(100%+1rem)] min-w-0 items-center gap-2 bg-[#f6f6f6] -mt-1.25! px-3 pb-2 text-[#52525c]">
                            <div className="flex min-w-0 flex-1 items-center gap-2" title={point.city}>
                                <span className="min-w-0 truncate font-bold! text-[16px]">{point.city}</span>
                            </div>

                            <FaXmark
                                size={22}
                                onClick={onClose}
                                className="shrink-0 -translate-y-px cursor-pointer"
                                title="Fechar"
                            />
                        </div>
                    )}
                >
                    <div className="relative w-full text-[14px] text-[#333]">
                        <div className="mt-3 mb-4 space-y-2 px-3">
                            <div>
                                <span className="text-gray-500">Data/hora: </span>
                                <span className="font-bold">{point.date || "Não informado"}</span>
                            </div>

                            <div className="chart-divisor my-2! m-0!"/>

                            <div>
                                <span className="text-gray-500">Local: </span>
                                <span className="font-bold">{formatLocal(point)}</span>
                            </div>

                            <div className={'flex flex-row items-center gap-1'}>
                                <span className="text-gray-500 mr-1">Sentido: </span>
                                {directionImage && (<img src={directionImage} alt="" className="h-4 w-4 object-contain"/>)}
                                <span className="font-bold">{point.laneDescLang || "Não informado"}</span>
                            </div>
                        </div>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

const SearchMarkersLayer = ({points, filterPlate}) => {
    const map = useMap();
    const [activeId, setActiveId] = useState(null);
    const {markers, toggleSpiderfy, setSpiderState} = useSpiderfy(points, map);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener("click", () => {
            setActiveId(null);
            setSpiderState(null);
        });

        return () => listener.remove();
    }, [map, setSpiderState]);

    const handleMarkerClick = (point) => {
        const result = toggleSpiderfy(point);

        if (result?.type === "spiderfy") {
            setActiveId(null);
            return;
        }

        setActiveId(point.id);
    };

    return (
        <>
            {markers.map((point) => (
                <SearchMarker
                    key={`sentry-cloud-search-marker-${point.id}`}
                    point={point}
                    filterPlate={filterPlate}
                    isOpen={activeId === point.id}
                    onOpen={handleMarkerClick}
                    onClose={() => setActiveId(null)}
                />
            ))}

            {markers.map((point) => {
                if (!point.original) return null;

                return (
                    <Polyline
                        key={`sentry-cloud-search-marker-line-${point.id}`}
                        path={[point.original, {lat: point.lat, lng: point.lng}]}
                        strokeColor="#333"
                        strokeOpacity={0.65}
                        strokeWeight={2}
                    />
                );
            })}
        </>
    );
};

const MapFitBounds = ({points}) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !points.length || !window.google) return;

        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((point) => bounds.extend(point.position));
        map.fitBounds(bounds, 48);
    }, [map, points]);

    return null;
};

const SentryCloudSearchMap = () => {
    const {collapsed, toggle} = useAppShell();
    const [searchParams] = useState(() => new URLSearchParams(window.location.search));
    const encodedFilters = searchParams.get("d");
    const filters = useMemo(() => decodeFilters(encodedFilters), [encodedFilters]);
    const collapsedByScreen = useRef(false);
    const [mapState, setMapState] = useState({
        neighborhoodStreets: true,
        pointsOfInterest: false,
    });

    const currentMap = useMemo(() => {
        if (mapState.pointsOfInterest && mapState.neighborhoodStreets) return "poi-street";
        if (mapState.pointsOfInterest && !mapState.neighborhoodStreets) return "poi-nostreet";
        if (!mapState.pointsOfInterest && !mapState.neighborhoodStreets) return "nopoi-nostreet";
        return "default";
    }, [mapState.neighborhoodStreets, mapState.pointsOfInterest]);

    useEffect(() => {
        if (collapsedByScreen.current) return;

        collapsedByScreen.current = true;
        if (!collapsed) toggle();
    }, [collapsed, toggle]);

    const {data: corporation = null, isLoading: isLoadingCorporation} = useQuery({
        queryKey: ["corporation"],
        queryFn: () => CorporationService.entity(),
    });

    const {data: searchResult = {}, isLoading: isLoadingSearch, isError} = useQuery({
        queryKey: ["sentry-cloud-search-map", encodedFilters],
        enabled: !!filters,
        queryFn: async () => {
            const payload = new FormData();
            payload.append("plate", filters.plate || "");
            payload.append("dtStart", filters.dtStart || "");
            payload.append("dtEnd", filters.dtEnd || "");
            payload.append("reason", filters.reason || "");

            const searchId = await sentryCloudSearchService.interligationCreate(payload);
            return sentryCloudSearchService.interligationResult(searchId);
        },
    });

    const points = useMemo(() => {
        const toNumber = (value) => {
            if (value === null || value === undefined || (typeof value === "string" && !value.trim())) return null;

            const number = Number(value);
            return Number.isFinite(number) ? number : null;
        };

        const parsePosition = (value) => {
            if (Array.isArray(value)) return {lat: value[0], lng: value[1]};

            if (typeof value === "string") {
                const serializedPosition = value.trim();
                if (!serializedPosition || serializedPosition === "[object Object]") return null;

                try {
                    return parsePosition(JSON.parse(serializedPosition));
                } catch {
                    const [lat, lng] = serializedPosition.split(",").map((coordinate) => coordinate.trim());
                    return {lat, lng};
                }
            }

            if (!value || typeof value !== "object") return null;

            const lat = typeof value.lat === "function" ? value.lat() : value.lat ?? value.latitude;
            const lng = typeof value.lng === "function" ? value.lng() : value.lng ?? value.longitude;

            return {lat, lng};
        };

        const normalizePosition = (value) => {
            const parsedPosition = parsePosition(value);
            if (!parsedPosition) return null;

            const latitude = toNumber(parsedPosition.lat);
            const longitude = toNumber(parsedPosition.lng);
            if (!isValidCoordinate(latitude, longitude)) return null;

            return {lat: latitude, lng: longitude};
        };

        return (searchResult?.result || []).map((item, index) => {
            const position = [
                {lat: item.lat, lng: item.lng},
                {lat: item.latitude, lng: item.longitude},
                item.position,
            ].map(normalizePosition).find(Boolean);

            if (!position) return null;

            return {
                ...item,
                id: `${position.lat}-${position.lng}-${index}`,
                lat: position.lat,
                lng: position.lng,
                position,
            };
        }).filter(Boolean);
    }, [searchResult]);

    const center = useMemo(() => points[0]?.position || {
        lat: Number(corporation?.latitude) || DEFAULT_CENTER.lat,
        lng: Number(corporation?.longitude) || DEFAULT_CENTER.lng,
    }, [corporation, points]);

    const statusMessage = isError ? "Não foi possível carregar o mapa para essa pesquisa." : !filters ? "Nenhum filtro de pesquisa foi informado."
        : !isLoadingSearch && !points.length ? "Nenhuma passagem encontrada." : null;

    const displayedPlate = filters.plate ?? "Não informado";

    return (
        <Template isLoading={isLoadingCorporation || isLoadingSearch}>
            <Template.Header>
                <Template.Title browserTitle="Passagens no mapa - SentryCloud">
                    Passagens no mapa - SentryCloud
                    <span className={'absolute right-3 text-[13px] bg-[#f6f6f6] border-[#f0f0f0] border-2 px-5 py-2 rounded-md font-black cursor-pointer transform -translate-y-[2px]'} title={`Placa pesquisada - ${displayedPlate}`}>{displayedPlate}</span>
                </Template.Title>
            </Template.Header>
            <Template.Body className="!p-0 h-[calc(100vh-57px)]">
                {statusMessage ? (
                    <div className="flex h-full items-center justify-center bg-[#f3f3f4] p-4 text-center text-[16px] text-gray-600">
                        {statusMessage}
                    </div>
                ) : corporation && (
                    <APIProvider apiKey={corporation.maps_api_key || ""} language="pt-BR">
                        <Map
                            id="sentry-cloud-search-map"
                            mapId={mapIds[currentMap]}
                            defaultCenter={center}
                            defaultZoom={13}
                            gestureHandling="greedy"
                            streetViewControl={false}
                            fullscreenControl={false}
                            style={{height: "100%", width: "100%"}}
                        >

                            <SentryCloudSearchMapElements mapState={mapState} setMapState={setMapState}/>
                            <MapFitBounds points={points}/>
                            <SearchMarkersLayer points={points} filterPlate={filters.plate}/>
                        </Map>

                    </APIProvider>
                )}
            </Template.Body>
        </Template>
    );
};

export default SentryCloudSearchMap;